import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { RouterDataResolved } from '@ngxs/router-plugin';
import {
	Action,
	Actions,
	NgxsOnInit,
	ofActionSuccessful,
	Selector,
	State,
	StateContext,
	Store,
} from '@ngxs/store';
import { TrackLocationService } from '@services/matter/track-location.service';
import { SessionService } from '@services/session/session.service';
import { AuthState } from '@state/auth';
import { UserProfileState } from '@state/user-profile';
import { cloneDeep } from 'lodash';
import { fromEvent } from 'rxjs';
import * as actions from './navigation.actions';
import { PAGE, SITE_SECTION } from './page.model';

export interface Navigation {
	url: string;
	matterId: string;
}

export interface NavigationStateModel {
	currentMatterId: string | null;
	history: Navigation[];
	url: string;
	page?: PAGE;
	section?: SITE_SECTION;
	queryParams?: { [key: string]: string };
}

@State<NavigationStateModel>({
	name: 'navigation',
	defaults: {
		currentMatterId: null,
		history: [],
		url: null,
		page: null,
		section: null,
		queryParams: {},
	},
})
@Injectable()
export class NavigationState implements NgxsOnInit {
	@Selector([NavigationState])
	static currentUrl(state: NavigationStateModel): string {
		return state.url;
	}

	@Selector([NavigationState])
	static getCurrentPage(state: NavigationStateModel): PAGE {
		return state.page;
	}

	@Selector([NavigationState])
	static getSiteSection(state: NavigationStateModel): SITE_SECTION {
		return state.section;
	}

	@Selector([NavigationState])
	static getQueryParams(state: NavigationStateModel): { [id: string]: string } {
		return state.queryParams;
	}

	constructor(
		private actions$: Actions,
		private sessionService: SessionService,
		private trackLocationService: TrackLocationService,
		private store: Store,
	) {}

	ngxsOnInit(ctx?: StateContext<NavigationStateModel>): void {
		this.actions$
			.pipe(ofActionSuccessful(RouterDataResolved))
			.subscribe((routerData: RouterDataResolved) => {
				const url = routerData?.routerState?.url;
				const matterId =
					routerData?.routerState?.root?.firstChild?.firstChild?.params
						?.matterId;

				let data;
				let obj: ActivatedRouteSnapshot = routerData.routerState.root;

				while (obj) {
					data = { ...data, ...obj.data };
					obj = obj?.firstChild;
				}

				ctx.dispatch(
					new actions.UrlChanged({
						matterId,
						url,
						data,
						queryParams: routerData.routerState.root.queryParams,
					}),
				);
			});

		fromEvent(document, 'visibilitychange').subscribe((event: Event) => {
			const { visibilityState } = event.target as Document;
			ctx.dispatch(
				new actions.OnVisibilityChanged({ visibility: visibilityState }),
			);
		});
	}

	@Action(actions.UrlChanged)
	storeUrl(
		{ getState, patchState }: StateContext<NavigationStateModel>,
		{ payload }: actions.UrlChanged,
	): void {
		const { history, currentMatterId } = getState();
		const page = payload.data.page;
		const section = payload.data.section;
		const queryParams = payload.queryParams;

		if (!payload.matterId) {
			patchState({
				currentMatterId: payload.matterId,
				url: payload.url,
				page,
				section,
				queryParams,
			});

			return;
		}

		const _history = cloneDeep(history);
		_history.push({ matterId: payload.matterId, url: payload.url });

		const previousMatterId = currentMatterId;

		if (previousMatterId && previousMatterId === payload.matterId) {
			this.trackLocationService.setCurrentMatterLocation(
				currentMatterId,
				payload.url,
			);
		} else {
			const _matterId = payload.matterId;

			patchState({
				currentMatterId: _matterId,
				history: _history,
				url: payload.url,
				page,
			});
		}

		patchState({
			history: _history,
			url: payload.url,
			page,
			section,
			queryParams,
		});
	}

	@Action(actions.OnVisibilityChanged)
	visibilityChanged(
		{ getState }: StateContext<NavigationStateModel>,
		{ payload }: actions.OnVisibilityChanged,
	) {
		const { currentBusinessId, id } =
			this.store.selectSnapshot(UserProfileState.getProfile) ?? {};
		const sessionId = this.store.selectSnapshot(
			AuthState.getLoggedInUser,
		)?.sessionId;

		if (!(id && currentBusinessId)) {
			console.log('Auth user was not set so i cannot write session to store');

			return;
		}

		if (payload.visibility === 'visible') {
			const { history } = getState();
			const lastItem = history[history.length - 1];

			return this.sessionService.setNavigationUrl(
				id,
				currentBusinessId,
				sessionId,
				lastItem?.url || null,
				lastItem?.matterId || null,
			);
		}

		return this.sessionService.deleteSession(currentBusinessId, sessionId);
	}
}
